import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import PaymentIcon from '@mui/icons-material/Payment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import DescriptionIcon from '@mui/icons-material/Description';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { DataService } from 'src/services/data-service';
import PrintOptionsDialog from 'src/pages/Receipts/PrintOptionsDialog';

interface Tenant {
  id: string;
  name: string;
}

interface Lease {
  id: string;
  refNumber: string;
}

interface FeeType {
  id: string;
  name: string;
  description: string;
  active: boolean;
}

interface Currency {
  id: string;
  name: string;
  code: string;
  active: boolean;
}

interface PaymentType {
  id: string;
  name: string;
  description: string;
  active: boolean;
}

enum PaymentMethod {
  Cash = 0,
  CreditCard = 1,
  DebitCard = 2,
  BankTransfer = 3,
  Check = 4,
  MobilePayment = 5,
  Other = 6
}

const PaymentMethodLabels: Record<PaymentMethod, string> = {
  [PaymentMethod.Cash]: 'Cash',
  [PaymentMethod.CreditCard]: 'Credit Card',
  [PaymentMethod.DebitCard]: 'Debit Card',
  [PaymentMethod.BankTransfer]: 'Bank Transfer',
  [PaymentMethod.Check]: 'Check',
  [PaymentMethod.MobilePayment]: 'Mobile Payment',
  [PaymentMethod.Other]: 'Other'
};

interface PaymentInstallmentItem {
  feeTypeId: string;
  currencyId: string;
  paymentTypeId: string;
  amount: number;
  method: PaymentMethod;
  notes: string;
}

interface StandalonePaymentDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const StandalonePaymentDialog: React.FC<StandalonePaymentDialogProps> = ({
  open,
  onClose,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [leasesLoading, setLeasesLoading] = useState(false);
  const [feeTypesLoading, setFeeTypesLoading] = useState(false);
  const [currenciesLoading, setCurrenciesLoading] = useState(false);
  const [paymentTypesLoading, setPaymentTypesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [leases, setLeases] = useState<Lease[]>([]);
  const [feeTypes, setFeeTypes] = useState<FeeType[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [paymentTypes, setPaymentTypes] = useState<PaymentType[]>([]);

  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const [selectedLeaseId, setSelectedLeaseId] = useState<string>('');

  const [paymentDate, setPaymentDate] = useState<string>(
    new Date().toISOString().slice(0, 16)
  );
  const [description, setDescription] = useState<string>('');
  const [installments, setInstallments] = useState<PaymentInstallmentItem[]>(
    []
  );

  // Print options dialog state
  const [printOptionsOpen, setPrintOptionsOpen] = useState(false);
  const [createdReceiptGroupRef, setCreatedReceiptGroupRef] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      fetchTenants();
      fetchFeeTypes();
      fetchCurrencies();
      fetchPaymentTypes();
      // Add one empty installment by default when dialog opens
      if (installments.length === 0) {
        setInstallments([
          {
            feeTypeId: '',
            currencyId: '',
            paymentTypeId: '',
            amount: 0,
            method: PaymentMethod.Cash,
            notes: ''
          }
        ]);
      }
    }
  }, [open]);

  useEffect(() => {
    if (selectedTenantId) {
      fetchLeases(selectedTenantId);
    } else {
      setLeases([]);
      setSelectedLeaseId('');
    }
  }, [selectedTenantId]);

  const fetchTenants = async () => {
    setTenantsLoading(true);
    try {
      const response = await DataService.get('api/Lov/tenants?onlyActive=true');

      if (response.ok) {
        const responseData = await response.json();

        if (responseData.success && responseData.data) {
          // Convert object to array
          const tenantsArray = Object.entries(responseData.data).map(([id, name]) => ({
            id,
            name: name as string
          }));
          setTenants(tenantsArray);
        }
      } else {
        throw new Error('Failed to fetch tenants');
      }
    } catch (error) {
      console.error('Error fetching tenants:', error);
      setError('Failed to load tenants. Please try again.');
    } finally {
      setTenantsLoading(false);
    }
  };

  const fetchLeases = async (tenantId: string) => {
    setLeasesLoading(true);
    try {
      const response = await DataService.get(`api/Lov/${tenantId}/leases?onlyActive=true`);

      if (response.ok) {
        const responseData = await response.json();

        if (responseData.success && responseData.data) {
          // Convert object to array
          const leasesArray = Object.entries(responseData.data).map(([id, refNumber]) => ({
            id,
            refNumber: refNumber as string
          }));
          setLeases(leasesArray);
        }
      } else {
        throw new Error('Failed to fetch leases');
      }
    } catch (error) {
      console.error('Error fetching leases:', error);
      setError('Failed to load leases. Please try again.');
    } finally {
      setLeasesLoading(false);
    }
  };

  const fetchFeeTypes = async () => {
    setFeeTypesLoading(true);
    try {
      const response = await DataService.get('api/fee-types/all');

      if (response.ok) {
        const responseData = await response.json();
        let typesList: FeeType[] = [];

        if (responseData.success && responseData.data) {
          typesList = responseData.data;
        } else if (Array.isArray(responseData)) {
          typesList = responseData;
        }

        // Filter only active fee types
        setFeeTypes(typesList.filter((type) => type.active));
      } else {
        throw new Error('Failed to fetch fee types');
      }
    } catch (error) {
      console.error('Error fetching fee types:', error);
      setError('Failed to load fee types. Please try again.');
    } finally {
      setFeeTypesLoading(false);
    }
  };

  const fetchCurrencies = async () => {
    setCurrenciesLoading(true);
    try {
      const response = await DataService.get('api/Currency/all');

      if (response.ok) {
        const responseData = await response.json();
        let currenciesList: Currency[] = [];

        if (responseData.success && responseData.data) {
          currenciesList = responseData.data;
        } else if (Array.isArray(responseData)) {
          currenciesList = responseData;
        }

        // Filter only active currencies
        setCurrencies(currenciesList.filter((currency) => currency.active));
      } else {
        throw new Error('Failed to fetch currencies');
      }
    } catch (error) {
      console.error('Error fetching currencies:', error);
      setError('Failed to load currencies. Please try again.');
    } finally {
      setCurrenciesLoading(false);
    }
  };

  const fetchPaymentTypes = async () => {
    setPaymentTypesLoading(true);
    try {
      const response = await DataService.get('api/payment-types/all');

      if (response.ok) {
        const responseData = await response.json();
        let typesList: PaymentType[] = [];

        if (responseData.success && responseData.data) {
          typesList = responseData.data;
        } else if (Array.isArray(responseData)) {
          typesList = responseData;
        }

        // Filter only active payment types
        setPaymentTypes(typesList.filter((type) => type.active));
      } else {
        throw new Error('Failed to fetch payment types');
      }
    } catch (error) {
      console.error('Error fetching payment types:', error);
      setError('Failed to load payment types. Please try again.');
    } finally {
      setPaymentTypesLoading(false);
    }
  };

  const handleInstallmentChange = (
    index: number,
    field: keyof PaymentInstallmentItem,
    value: string | number
  ) => {
    const updatedInstallments = [...installments];
    updatedInstallments[index] = {
      ...updatedInstallments[index],
      [field]: value
    };
    setInstallments(updatedInstallments);
  };

  const handleAddInstallment = () => {
    setInstallments([
      ...installments,
      {
        feeTypeId: '',
        currencyId: '',
        paymentTypeId: '',
        amount: 0,
        method: PaymentMethod.Cash,
        notes: ''
      }
    ]);
  };

  const handleRemoveInstallment = (index: number) => {
    const updatedInstallments = installments.filter((_, i) => i !== index);
    setInstallments(updatedInstallments);
  };

  const validateForm = (): boolean => {
    // Check if tenant is selected
    if (!selectedTenantId) {
      setError('Please select a tenant.');
      return false;
    }

    // Check if lease is selected
    if (!selectedLeaseId) {
      setError('Please select a lease.');
      return false;
    }

    // Check if payment date is provided
    if (!paymentDate) {
      setError('Payment date is required.');
      return false;
    }

    // Check if at least one installment is added
    if (installments.length === 0) {
      setError('Please add at least one installment.');
      return false;
    }

    // Validate each installment
    for (let i = 0; i < installments.length; i++) {
      const item = installments[i];

      if (!item.feeTypeId) {
        setError(`Please select a fee type for installment #${i + 1}.`);
        return false;
      }

      if (!item.currencyId) {
        setError(`Please select a currency for installment #${i + 1}.`);
        return false;
      }

      if (!item.paymentTypeId) {
        setError(`Please select a payment type for installment #${i + 1}.`);
        return false;
      }

      if (!item.amount || item.amount <= 0) {
        setError(`Please enter a valid amount for installment #${i + 1}.`);
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setError(null);

    try {
      // Step 1: Prepare payload for API
      const paymentPayload = installments.map((item) => ({
        currencyId: item.currencyId,
        paymentTypeId: item.paymentTypeId,
        feeTypeId: item.feeTypeId,
        amount: item.amount,
        paidAt: new Date(paymentDate).toISOString(),
        method: item.method
      }));

      // Step 2: Validate payment with simulate API
      setValidating(true);
      const simulateResponse = await DataService.post(
        `api/Installment/simulate-pay?leaseId=${selectedLeaseId}`,
        paymentPayload
      );

      if (!simulateResponse.ok) {
        const errorData = await simulateResponse.json();
        throw new Error(
          errorData.Message || errorData.message || 'Payment validation failed'
        );
      }

      const simulateData = await simulateResponse.json();

      // Check if validation was successful (handle both uppercase and lowercase)
      const isSuccess =
        simulateData.Success !== undefined
          ? simulateData.Success
          : simulateData.success;
      if (!isSuccess) {
        throw new Error(
          simulateData.Message ||
            simulateData.message ||
            'Payment validation failed'
        );
      }

      setValidating(false);

      // Step 3: Submit actual payment
      setLoading(true);
      const payResponse = await DataService.post(
        `api/Installment/pay?leaseId=${selectedLeaseId}`,
        paymentPayload
      );

      if (!payResponse.ok) {
        const errorData = await payResponse.json();
        throw new Error(
          errorData.Message || errorData.message || 'Failed to submit payment'
        );
      }

      const payData = await payResponse.json();

      // Check if payment was successful (handle both uppercase and lowercase)
      const isPaySuccess =
        payData.Success !== undefined ? payData.Success : payData.success;
      if (!isPaySuccess) {
        throw new Error(
          payData.Message || payData.message || 'Failed to submit payment'
        );
      }

      // Extract receipt group reference from response
      console.log('Payment response data:', payData);

      const receiptGroupRefNumber =
        payData.Data?.receiptGroupReference ||
        payData.data?.receiptGroupReference ||
        payData.Data?.receiptGroupRefNumber ||
        payData.data?.receiptGroupRefNumber ||
        payData.Data?.refNumber ||
        payData.data?.refNumber;

      console.log('Extracted receipt group reference:', receiptGroupRefNumber);

      // Show print options dialog if receipt group ref exists
      if (receiptGroupRefNumber) {
        console.log('Opening print options dialog for receipt:', receiptGroupRefNumber);
        setCreatedReceiptGroupRef(receiptGroupRefNumber);
        setPrintOptionsOpen(true);
        // Don't close the dialog yet - let the user interact with print options first
        // We'll call onSuccess when the print dialog closes
      } else {
        console.log('No receipt reference found in response');
        // No receipt created, just refresh and close
        onSuccess();
        handleClose();
      }
    } catch (error) {
      console.error('Error submitting payment:', error);
      setError(
        error instanceof Error
          ? error.message
          : 'Failed to submit payment. Please try again.'
      );
      setValidating(false);
      setLoading(false);
    } finally {
      setValidating(false);
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading && !validating) {
      setError(null);
      setSelectedTenantId('');
      setSelectedLeaseId('');
      setPaymentDate(new Date().toISOString().slice(0, 16));
      setDescription('');
      setInstallments([]);
      onClose();
    }
  };

  return (
    <>
      {/* Print Options Dialog */}
      <PrintOptionsDialog
        open={printOptionsOpen}
        onClose={() => {
          setPrintOptionsOpen(false);
          setCreatedReceiptGroupRef(null);
          // Now that print dialog is closed, refresh and close the main dialog
          onSuccess();
          handleClose();
        }}
        receiptGroupRefNumber={createdReceiptGroupRef}
      />

      {/* Manual Payment Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            fontWeight: 'bold',
            py: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PaymentIcon />
            Create Manual Receipt
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{
            pt: 3,
            pb: 2,
            minHeight: '400px',
            maxHeight: '70vh',
            overflowY: 'auto'
          }}
        >
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Tenant & Lease Selection Section */}
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
            <Typography
              variant="h6"
              fontWeight="bold"
              color="primary.main"
              sx={{ mb: 2 }}
            >
              Select Tenant & Lease
            </Typography>

            <Grid container spacing={2}>
              {/* Tenant Selection */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" required>
                  <InputLabel>Tenant</InputLabel>
                  <Select
                    value={selectedTenantId}
                    onChange={(e) => setSelectedTenantId(e.target.value)}
                    label="Tenant"
                    disabled={tenantsLoading}
                    startAdornment={<PersonIcon color="primary" sx={{ mr: 1 }} />}
                  >
                    <MenuItem value="" disabled>
                      Select Tenant
                    </MenuItem>
                    {tenants.map((tenant) => (
                      <MenuItem key={tenant.id} value={tenant.id}>
                        {tenant.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Lease Selection */}
              <Grid item xs={12} sm={6}>
                <FormControl
                  fullWidth
                  size="small"
                  required
                  disabled={!selectedTenantId || leasesLoading}
                >
                  <InputLabel>Lease</InputLabel>
                  <Select
                    value={selectedLeaseId}
                    onChange={(e) => setSelectedLeaseId(e.target.value)}
                    label="Lease"
                    startAdornment={<DescriptionIcon color="primary" sx={{ mr: 1 }} />}
                  >
                    <MenuItem value="" disabled>
                      {!selectedTenantId ? 'Select tenant first' : 'Select Lease'}
                    </MenuItem>
                    {leases.map((lease) => (
                      <MenuItem key={lease.id} value={lease.id}>
                        {lease.refNumber}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          {/* Payment Information Section */}
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
            <Typography
              variant="h6"
              fontWeight="bold"
              color="primary.main"
              sx={{ mb: 2 }}
            >
              Receipt Information
            </Typography>

            <Grid container spacing={2}>
              {/* Payment Date */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Payment Date & Time"
                  type="datetime-local"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  InputLabelProps={{
                    shrink: true
                  }}
                  size="small"
                  required
                />
              </Grid>

              {/* Description */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Description (Optional)"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Add payment description..."
                  size="small"
                />
              </Grid>
            </Grid>
          </Paper>

          <Divider sx={{ mb: 2 }} />

          {/* Installments Section */}
          <Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2
              }}
            >
              <Typography
                variant="h6"
                fontWeight="bold"
                color="primary.main"
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <AttachMoneyIcon />
                Receipt Details ({installments.length})
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddInstallment}
                size="small"
                disabled={
                  feeTypesLoading || currenciesLoading || paymentTypesLoading || !selectedLeaseId
                }
              >
                Add Fee
              </Button>
            </Box>

            {feeTypesLoading || currenciesLoading || paymentTypesLoading ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '150px'
                }}
              >
                <CircularProgress />
              </Box>
            ) : installments.length === 0 ? (
              <Alert severity="info">
                No installments added yet. Click "Add Fee" to add payment
                items.
              </Alert>
            ) : (
              <TableContainer
                component={Paper}
                sx={{ maxHeight: 'calc(70vh - 350px)', overflowY: 'auto' }}
              >
                <Table
                  size="small"
                  sx={{ '& .MuiTableCell-root': { py: 1, px: 1 } }}
                  stickyHeader
                >
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell
                        sx={{
                          fontWeight: 'bold',
                          width: '3%',
                          bgcolor: 'grey.100'
                        }}
                      >
                        #
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 'bold',
                          width: '18%',
                          bgcolor: 'grey.100'
                        }}
                      >
                        Fee Type
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 'bold',
                          width: '10%',
                          bgcolor: 'grey.100'
                        }}
                      >
                        Currency
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 'bold',
                          width: '18%',
                          bgcolor: 'grey.100'
                        }}
                      >
                        Payment Type
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 'bold',
                          width: '12%',
                          bgcolor: 'grey.100'
                        }}
                      >
                        Method
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 'bold',
                          width: '12%',
                          bgcolor: 'grey.100'
                        }}
                      >
                        Amount
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 'bold',
                          width: '22%',
                          bgcolor: 'grey.100'
                        }}
                      >
                        Notes
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 'bold',
                          width: '5%',
                          textAlign: 'center',
                          bgcolor: 'grey.100'
                        }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {installments.map((installment, index) => (
                      <TableRow key={index} hover>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          <FormControl fullWidth size="small" required>
                            <Select
                              value={installment.feeTypeId}
                              onChange={(e) =>
                                handleInstallmentChange(
                                  index,
                                  'feeTypeId',
                                  e.target.value
                                )
                              }
                              displayEmpty
                            >
                              <MenuItem value="" disabled>
                                Select Fee Type
                              </MenuItem>
                              {feeTypes.map((type) => (
                                <MenuItem key={type.id} value={type.id}>
                                  {type.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <FormControl fullWidth size="small" required>
                            <Select
                              value={installment.currencyId}
                              onChange={(e) =>
                                handleInstallmentChange(
                                  index,
                                  'currencyId',
                                  e.target.value
                                )
                              }
                              displayEmpty
                            >
                              <MenuItem value="" disabled>
                                Select Currency
                              </MenuItem>
                              {currencies.map((currency) => (
                                <MenuItem key={currency.id} value={currency.id}>
                                  {currency.code}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <FormControl fullWidth size="small" required>
                            <Select
                              value={installment.paymentTypeId}
                              onChange={(e) =>
                                handleInstallmentChange(
                                  index,
                                  'paymentTypeId',
                                  e.target.value
                                )
                              }
                              displayEmpty
                            >
                              <MenuItem value="" disabled>
                                Select Payment Type
                              </MenuItem>
                              {paymentTypes.map((type) => (
                                <MenuItem key={type.id} value={type.id}>
                                  {type.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <FormControl fullWidth size="small" required>
                            <Select
                              value={installment.method}
                              onChange={(e) =>
                                handleInstallmentChange(
                                  index,
                                  'method',
                                  parseInt(e.target.value as string)
                                )
                              }
                            >
                              {Object.entries(PaymentMethodLabels).map(
                                ([value, label]) => (
                                  <MenuItem key={value} value={parseInt(value)}>
                                    {label}
                                  </MenuItem>
                                )
                              )}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            value={installment.amount || ''}
                            onChange={(e) =>
                              handleInstallmentChange(
                                index,
                                'amount',
                                parseFloat(e.target.value) || 0
                              )
                            }
                            placeholder="0.00"
                            inputProps={{ min: 0, step: 0.01 }}
                            required
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            value={installment.notes}
                            onChange={(e) =>
                              handleInstallmentChange(
                                index,
                                'notes',
                                e.target.value
                              )
                            }
                            placeholder="Add notes..."
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveInstallment(index)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleClose}
            disabled={loading || validating}
            variant="outlined"
            size="small"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            size="small"
            disabled={
              loading ||
              validating ||
              feeTypesLoading ||
              currenciesLoading ||
              paymentTypesLoading ||
              installments.length === 0 ||
              !selectedTenantId ||
              !selectedLeaseId
            }
            startIcon={
              loading || validating ? (
                <CircularProgress size={20} />
              ) : (
                <CheckCircleIcon />
              )
            }
          >
            {validating
              ? 'Validating Payment...'
              : loading
              ? 'Processing Payment...'
              : 'Submit Payment'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default StandalonePaymentDialog;
